import React, { useState } from 'react';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import Select from 'react-select';
import classNames from 'classnames';

import CommonButtonDefault from '../../../components/common/buttons/default';
import { useCompany } from '../../../hooks/use-company';
import StyledSwitch from '../../../components/common/form/styled-switch';
import CommonAnimatedLoader from '../../../components/common/animated/loader';
import AlertPrimary from '../../../components/common/alerts/alert-primary';
import StyledError from '../../../components/common/form/styled-error';
import { strings } from '../../../localization/strings';
import StyledInput from '../../../components/common/form/styled-input';
import StyledLabel from '../../../components/common/form/styled-label';
import { Language } from '../../../entities/company-admin-structure';
import { Category } from '../../../entities/category';
import ExportNewsForm from '../../../entities/form/export-news';
import * as companiesService from '../../../services/api/companies';
import { useAuth } from '../../../hooks/use-auth';
import CompaniesReexportNewsModal from './export-reexport-modal';

const exportNewsTypes = [
  { value: 1, label: 'FTP' },
  { value: 2, label: 'El. paštas' },
  { value: 3, label: 'API' },
];

const exportPressreleaseTypes = [
  { value: 1, label: 'FTP' },
  { value: 2, label: 'El. paštas' },
  { value: 3, label: 'API' },
];

const exportNewsFormats = [
  { value: 1, label: 'XML (UTF-8)' },
  { value: 2, label: 'HTML (UTF-8)' },
  { value: 3, label: 'PLAIN TEXT' },
  { value: 5, label: 'PLAIN TEXT ATTACHMENT (Sena koduotė)' },
  { value: 7, label: 'JSON' },
];

const exportNewsFormatsFTP = [
  { value: 1, label: 'XML (UTF-8)' },
  { value: 4, label: 'PLAIN TEXT (UTF-8)' },
  { value: 7, label: 'JSON' },
];

const exportNewsEncoding = [
  { value: 1, label: 'UTF-8' },
  { value: 2, label: 'Sena koduotė' },
];

const countryOptions = [
  { value: 1, label: 'Lietuvos' },
  { value: 2, label: 'Užsienio' },
];

function ExportNews() {
  const { token } = useAuth();
  const { companyDetails: company, updateCompany, companyStructure: structure } = useCompany();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<ExportNewsForm>({
    defaultValues: {
      export_news: company.export_news,
      export_type: company.export_type,
      export_news_format: company.export_news_format,
      export_news_encoding: company.export_news_encoding,
      export_news_ftp_port: company.export_news_ftp_port,
      export_news_ftp_username: company.export_news_ftp_username,
      export_news_ftp_password: company.export_news_ftp_password,
      export_news_ftp_address: company.export_news_ftp_address,
      export_news_use_sftp: company.export_news_use_sftp,
      export_news_url: company.export_news_url,
      export_news_headers: company.export_news_headers,
      export_news_categories: company.export_news_categories,
      can_export_pressrelease: company.can_export_pressrelease,
      export_pressreleases_type: company.export_pressreleases_type,
      export_pressreleases_url: company.export_pressreleases_url,
      export_pressreleases_headers: company.export_pressreleases_headers,
    },
  });
  const {
    fields: exportCategories,
    append: appendExportCategory,
    remove: removeExportCategory,
  } = useFieldArray({ control, name: 'export_news_categories' });

  const onSubmit: SubmitHandler<ExportNewsForm> = async (data) => {
    try {
      setIsLoading(true);
      setError('');

      const res = await companiesService.update(token, company?.id, data);

      if (res) {
        setIsLoading(false);
        setIsSubmitSuccessful(true);

        updateCompany(data);

        setTimeout(() => {
          setIsSubmitSuccessful(false);
        }, 2000);
      }
    } catch (e) {
      setIsLoading(false);
      if (e instanceof Error) {
        setError(e.message);
      }
    }
  };

  const getAllCategories = (languageId: number) => {
    if (!structure.languages[languageId]) {
      return [];
    }

    return [...structure.languages[languageId].categories, ...structure.languages[languageId].guideCategories];
  };

  const findCategoryById = (languageId: number, id?: number | null): Category | undefined => {
    if (id === undefined || id === null) {
      return undefined;
    }

    for (const category of getAllCategories(languageId)) {
      if (category.id === id) {
        return category;
      }
      const sub = category.sub_category?.find((subCategory) => subCategory.id === id);

      if (sub) {
        return sub;
      }
    }

    return undefined;
  };

  const resolveCategoryValue = (languageId: number, id?: number | null): Category | null => {
    if (id === undefined || id === null) {
      return null;
    }

    const known = findCategoryById(languageId, id);

    if (known) {
      return known;
    }

    return { id, name: `⚠ Nežinoma kategorija (#${id})` } as Category;
  };

  return isLoading ? (
    <CommonAnimatedLoader />
  ) : (
    <>
      {error && (
        <div className="max-w-7xl mx-auto my-5">
          <AlertPrimary type="danger" text={error} />
        </div>
      )}

      {isSubmitSuccessful && (
        <div className="mb-2 mt-4">
          <AlertPrimary size="xs" type="success" text="Sėkmingai atnaujinta" />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="bg-white sm:rounded-lg ring-1 ring-gray-900/5">
          <div className="p-4 font-medium leading-6 bg-gray-50 text-gray-900 flex flex-row sm:rounded-t-lg">
            <h3>Naujienų eksportavimas</h3>
            {watch('export_news') === 1 && (
              <div className="ml-auto">
                <CompaniesReexportNewsModal />
              </div>
            )}
            <div className={classNames(watch('export_news') ? 'ml-4' : 'ml-auto')}>
              <CommonButtonDefault type="submit" xs primary>
                {strings.button.save}
              </CommonButtonDefault>
            </div>
            <div className="ml-4">
              <StyledSwitch name="export_news" control={control} />
            </div>
          </div>

          {watch('export_news') === 1 && (
            <div>
              <div className="grid grid-cols-4 gap-4 p-4">
                <div className="col-span-2">
                  <StyledLabel htmlFor="export_type">Tipas</StyledLabel>
                  <div className="mt-1">
                    <Controller
                      control={control}
                      name="export_type"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Select
                          options={exportNewsTypes}
                          placeholder="Pasirinkite tipą"
                          value={exportNewsTypes.find((option) => option.value === field.value)}
                          onChange={(e) => {
                            field.onChange(e?.value);
                          }}
                        />
                      )}
                    />
                    {errors.export_type && <StyledError>{strings.error.fieldIsRequired}</StyledError>}
                  </div>
                </div>
                <div className="col-span-2">
                  <StyledLabel htmlFor="export_news_format">Formatas</StyledLabel>
                  <div className="mt-1">
                    {watch('export_type') === 2 ? (
                      <Controller
                        control={control}
                        name="export_news_format"
                        rules={{ required: true }}
                        render={({ field }) => (
                          <Select
                            options={exportNewsFormats}
                            placeholder="Pasirinkite siunčiamos informacijos formatą"
                            value={exportNewsFormats.find((option) => option.value === field.value)}
                            onChange={(e) => {
                              field.onChange(e?.value);
                            }}
                          />
                        )}
                      />
                    ) : (
                      <Controller
                        control={control}
                        name="export_news_format"
                        rules={{ required: true }}
                        render={({ field }) => (
                          <Select
                            options={exportNewsFormatsFTP}
                            placeholder="Pasirinkite siunčiamos informacijos formatą"
                            value={exportNewsFormatsFTP.find((option) => option.value === field.value)}
                            onChange={(e) => {
                              field.onChange(e?.value);
                            }}
                          />
                        )}
                      />
                    )}
                  </div>
                </div>
                {watch('export_news_format') === 3 && (
                  <div className="col-span-2">
                    <StyledLabel>Koduotė</StyledLabel>

                    <Controller
                      control={control}
                      name="export_news_encoding"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Select
                          options={exportNewsEncoding}
                          placeholder="Pasirinkite siunčiamos informacijos koduotę"
                          value={exportNewsEncoding.find((option) => option.value === field.value)}
                          onChange={(e) => {
                            field.onChange(e?.value);
                          }}
                        />
                      )}
                    />
                    {errors.export_news_encoding && <StyledError>{strings.error.fieldIsRequired}</StyledError>}
                  </div>
                )}
                {watch('export_type') === 1 && (
                  <>
                    <div className="col-span-4 mt-2 flex items-center gap-2">
                      <input
                        id="export_news_use_sftp"
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        {...register('export_news_use_sftp')}
                      />
                      <StyledLabel htmlFor="export_news_use_sftp" className="!mb-0">
                        Naudoti SFTP (vietoje FTP)
                      </StyledLabel>
                    </div>
                    <div className="col-span-1 mt-2">
                      <StyledLabel htmlFor="export_news_ftp_address">Serverio adresas</StyledLabel>

                      <div className="mt-1">
                        <StyledInput
                          id="export_news_ftp_address"
                          className={` ${errors.export_news_ftp_address && `!border-red-500`} `}
                          type="text"
                          {...register('export_news_ftp_address')}
                        />
                      </div>
                    </div>
                    <div className="col-span-1 mt-2">
                      <StyledLabel htmlFor="export_news_ftp_port">Serverio prievadas</StyledLabel>
                      <div className="mt-1">
                        <StyledInput
                          id="export_news_ftp_port"
                          className={` ${errors.export_news_ftp_port && `!border-red-500`} `}
                          type="text"
                          {...register('export_news_ftp_port')}
                        />
                      </div>
                    </div>
                    <div className="col-span-1 mt-2">
                      <StyledLabel htmlFor="export_news_ftp_username">Serverio vartotojo vardas</StyledLabel>
                      <div className="mt-1">
                        <StyledInput
                          id="export_news_ftp_username"
                          className={` ${errors.export_news_ftp_username && `!border-red-500`} `}
                          type="text"
                          {...register('export_news_ftp_username')}
                        />
                      </div>
                    </div>
                    <div className="col-span-1 mt-2">
                      <StyledLabel htmlFor="export_news_ftp_password">Serverio slaptažodis</StyledLabel>
                      <div className="mt-1">
                        <StyledInput
                          id="export_news_ftp_password"
                          className={` ${errors.export_news_ftp_username && `!border-red-500`} `}
                          type="text"
                          {...register('export_news_ftp_password')}
                        />
                      </div>
                    </div>
                  </>
                )}
                {watch('export_type') === 3 && (
                  <>
                    <div className="col-span-2 mt-2">
                      <StyledLabel htmlFor="export_news_url">API URL</StyledLabel>
                      <div className="mt-1">
                        <StyledInput
                          id="export_news_url"
                          className={` ${errors.export_news_url && `!border-red-500`} `}
                          type="text"
                          placeholder="https://example.com/api/news"
                          {...register('export_news_url', {
                            required: watch('export_type') === 3,
                          })}
                        />
                        {errors.export_news_url && <StyledError>{strings.error.fieldIsRequired}</StyledError>}
                      </div>
                    </div>
                    <div className="col-span-4 mt-2">
                      <StyledLabel htmlFor="export_news_headers">API Headers (JSON formatas)</StyledLabel>
                      <div className="mt-1">
                        <textarea
                          id="export_news_headers"
                          className={`min-h-[100px] block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
                            errors.export_news_headers && `!border-red-500`
                          }`}
                          placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
                          {...register('export_news_headers', {
                            required: watch('export_type') === 3,
                          })}
                        />
                        {errors.export_news_headers && <StyledError>{strings.error.fieldIsRequired}</StyledError>}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="border-t border-gray-900/5">
                <h3 className="p-4 font-medium">Eksportuojamas turinys</h3>
                {exportCategories.map((item, index) => (
                  <div key={`fields-export-${item.id}`} className="flex flex-row pb-2 px-4">
                    <div
                      className={classNames(
                        'grid gap-4 grow',
                        watch('export_type') === 2 ? 'grid-cols-4' : 'grid-cols-3',
                      )}
                    >
                      <div className="col-span-1">
                        <Controller
                          defaultValue={0}
                          control={control}
                          name={`export_news_categories.${index}.lang`}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <Select
                              options={structure?.languages}
                              getOptionValue={(option: Language) => `${option.id}`}
                              getOptionLabel={(option: Language) => `${option.name}`}
                              placeholder="Pasirinkite kalbą"
                              value={structure?.languages.find((option) => option.id === field.value)}
                              onChange={(e) => {
                                field.onChange(e?.id);
                              }}
                            />
                          )}
                        />
                      </div>
                      <div className="col-span-1">
                        <Controller
                          control={control}
                          name={`export_news_categories.${index}.country`}
                          defaultValue={null}
                          render={({ field }) => (
                            <Select
                              options={countryOptions}
                              placeholder="Pasirinkite šalį"
                              isClearable
                              value={countryOptions.find((option) => option.value === field.value) ?? null}
                              onChange={(option) => {
                                field.onChange((option ? option.value : null) as number | undefined);
                              }}
                            />
                          )}
                        />
                      </div>
                      <div className="col-span-1">
                        <Controller
                          control={control}
                          name={`export_news_categories.${index}.category_id`}
                          defaultValue={null}
                          render={({ field }) => (
                            <Select
                              options={getAllCategories(watch(`export_news_categories.${index}.lang`))}
                              placeholder="Pasirinkite temą"
                              isClearable
                              getOptionValue={(option: Category) => `${option.id}`}
                              getOptionLabel={(option: Category) => `${option.name}`}
                              value={resolveCategoryValue(watch(`export_news_categories.${index}.lang`), field.value)}
                              onChange={(option) => {
                                field.onChange((option ? option.id : null) as number | undefined);
                              }}
                            />
                          )}
                        />
                      </div>
                      {watch('export_type') === 2 && (
                        <div className="col-span-1">
                          <StyledInput
                            id={`export_news_categories.${index}.email`}
                            type="text"
                            noValidation
                            placeholder="El.pašto adresai, atskirti kableliu"
                            {...register(`export_news_categories.${index}.email`)}
                          />
                        </div>
                      )}
                    </div>
                    <div className="pl-4">
                      <CommonButtonDefault
                        type="button"
                        onClick={() => removeExportCategory(index)}
                        sm
                        danger
                        className="ml-auto"
                      >
                        Ištrinti
                      </CommonButtonDefault>
                    </div>
                  </div>
                ))}
                <div className="p-4 pt-0 w-full">
                  <CommonButtonDefault
                    sm
                    onClick={() =>
                      appendExportCategory({ lang: 0, type: 0, email: '', category_id: null, country: null })
                    }
                    type="button"
                    secondary
                  >
                    Pridėti
                  </CommonButtonDefault>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Press Release Export Section */}
        <div className="bg-white sm:rounded-lg ring-1 ring-gray-900/5 mt-6">
          <div className="p-4 font-medium leading-6 bg-gray-50 text-gray-900 flex flex-row sm:rounded-t-lg">
            <h3>Pranešimų spaudai eksportavimas</h3>
            <div className={classNames(watch('can_export_pressrelease') ? 'ml-auto' : 'ml-auto')}>
              <CommonButtonDefault type="submit" xs primary>
                {strings.button.save}
              </CommonButtonDefault>
            </div>
            <div className="ml-4">
              <StyledSwitch name="can_export_pressrelease" control={control} />
            </div>
          </div>

          {watch('can_export_pressrelease') === 1 && (
            <div>
              <div className="grid grid-cols-4 gap-4 p-4">
                <div className="col-span-2">
                  <StyledLabel htmlFor="export_pressreleases_type">Tipas</StyledLabel>
                  <div className="mt-1">
                    <Controller
                      control={control}
                      name="export_pressreleases_type"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Select
                          options={exportPressreleaseTypes}
                          placeholder="Pasirinkite tipą"
                          value={exportPressreleaseTypes.find((option) => option.value === field.value)}
                          onChange={(e) => {
                            field.onChange(e?.value);
                          }}
                        />
                      )}
                    />
                    {errors.export_pressreleases_type && <StyledError>{strings.error.fieldIsRequired}</StyledError>}
                  </div>
                </div>
                {watch('export_pressreleases_type') === 3 && (
                  <>
                    <div className="col-span-2 mt-2">
                      <StyledLabel htmlFor="export_pressreleases_url">API URL</StyledLabel>
                      <div className="mt-1">
                        <StyledInput
                          id="export_pressreleases_url"
                          className={` ${errors.export_pressreleases_url && `!border-red-500`} `}
                          type="text"
                          placeholder="https://example.com/api/pressreleases"
                          {...register('export_pressreleases_url', {
                            required: watch('export_pressreleases_type') === 3,
                          })}
                        />
                        {errors.export_pressreleases_url && <StyledError>{strings.error.fieldIsRequired}</StyledError>}
                      </div>
                    </div>
                    <div className="col-span-4 mt-2">
                      <StyledLabel htmlFor="export_pressreleases_headers">API Headers (JSON formatas)</StyledLabel>
                      <div className="mt-1">
                        <textarea
                          id="export_pressreleases_headers"
                          className={`min-h-[100px] block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
                            errors.export_pressreleases_headers && `!border-red-500`
                          }`}
                          placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
                          {...register('export_pressreleases_headers', {
                            required: watch('export_pressreleases_type') === 3,
                          })}
                        />
                        {errors.export_pressreleases_headers && (
                          <StyledError>{strings.error.fieldIsRequired}</StyledError>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </form>
    </>
  );
}

export default ExportNews;
